import React, { useState } from "react";
import { Btn } from "../../styles/Button.styled";
import { Container } from "../../styles/Sidebar/UploadModal.styled";
import Popup from "../Popup/Popup";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Input } from "../inputs/Input";
import { CircularProgress } from "@mui/material";
import { addContacts } from "../../actions/contacts";

interface ContactAccount {
  email: string;
  code: string;
  firstname: string;
  lastname: string;
  middlename: string;
}

interface AccountErrors {
  email?: string;
  code?: string;
  lastname?: string;
  firstname?: string;
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const INITIAL_ACCOUNT: ContactAccount = {
  email: "",
  code: "",
  firstname: "",
  lastname: "",
  middlename: "",
};

export function CreateContactPopup({
  handleClose,
  onSuccess,
}: {
  handleClose: () => void;
  onSuccess?: () => void;
}) {
  const dispatch = useAppDispatch();
  const contactsPostState = useAppSelector((state) => state.contactsPost);

  const [account, setAccount] = useState<ContactAccount>(INITIAL_ACCOUNT);
  const [errors, setErrors] = useState<AccountErrors & { system?: string }>({});

  const handleChange = (field: keyof ContactAccount, value: string) => {
    setAccount((prev) => ({ ...prev, [field]: value }));

    let fieldError: string | undefined;

    if (field === "email") {
      if (!value.trim()) fieldError = "Вкажіть email";
      else if (!EMAIL_REGEX.test(value)) fieldError = "Невірний формат email";
    }
    if (field === "code") {
      const digits = value.replace(/\D/g, "");
      if (!digits) fieldError = "Вкажіть ІПН";
      else if (digits.length < 8) fieldError = "Мінімум 8 цифр";
    }
    if (field === "lastname" && !value.trim()) fieldError = "Прізвище обов'язкове";
    if (field === "firstname" && !value.trim()) fieldError = "Ім'я обов'язкове";

    setErrors((prev) => ({ ...prev, [field]: fieldError }));
  };

  const validate = (): boolean => {
    const newErrors: AccountErrors = {};

    if (!account.email.trim() || !EMAIL_REGEX.test(account.email))
      newErrors.email = "Невірний email";
    if (!account.code.replace(/\D/g, ""))
      newErrors.code = "Вкажіть ІПН";
    if (!account.lastname.trim())
      newErrors.lastname = "Вкажіть прізвище";
    if (!account.firstname.trim())
      newErrors.firstname = "Вкажіть ім'я";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const result = await dispatch(addContacts(account) as any);
      if (result?.payload?.message) {
        setErrors((prev) => ({ ...prev, system: result.payload.message }));
        return;
      }
      onSuccess?.();
      handleClose();
    } catch {
      setErrors((prev) => ({ ...prev, system: "Помилка з'єднання з сервером" }));
    }
  };

  const isFormFilled =
    account.email.trim() &&
    account.code.trim() &&
    account.lastname.trim() &&
    account.firstname.trim();

  const hasValidationErrors = Object.values(errors).some(Boolean);

  return (
    <Popup
      clickCloseButton={handleClose}
      style={{ height: "max-content", width: "80vw", maxWidth: "800px" }}
    >
      <h1 style={{ marginBottom: 16 }}>Створити контакт контрагента</h1>
      <Container>
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          padding: "16px",
          backgroundColor: "var(--disabled-bg)",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          marginBottom: "16px",
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <Input
              label="Email контрагента"
              type="email"
              placeholder="example@mail.com"
              value={account.email}
              onChange={(val) => handleChange("email", val)}
              error={errors.email}
            />
            <Input
              label="ЄДРПОУ/ІПН компанії контрагента"
              placeholder="Введіть код"
              type="number"
              maxNumber={10}
              value={account.code}
              onChange={(val) => handleChange("code", val)}
              error={errors.code}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
            <Input
              label="Прізвище"
              placeholder="Прізвище"
              value={account.lastname}
              onChange={(val) => handleChange("lastname", val)}
              error={errors.lastname}
            />
            <Input
              label="Ім'я"
              placeholder="Ім'я"
              value={account.firstname}
              onChange={(val) => handleChange("firstname", val)}
              error={errors.firstname}
            />
            <Input
              label="По батькові"
              placeholder="По батькові"
              value={account.middlename}
              onChange={(val) => handleChange("middlename", val)}
            />
          </div>
        </div>

        <div style={{ color: "var(--vivid-pink)", fontSize: "13px", minHeight: "16px", marginBottom: 4 }}>
          {errors.system}
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <Btn
            type="button"
            $width="100%"
            $color="var(--blue)"
            $marginTop="0"
            style={{ flex: 1 }}
            onClick={handleClose}
            $background="var(--sidebar-hover)"
            $heightMobile="47px"
          >
            Відмінити
          </Btn>
          <Btn
            style={{ flex: 1 }}
            type="button"
            $width="100%"
            $color="var(--white)"
            $marginTop="0"
            onClick={handleSave}
            $background="var(--blue)"
            $heightMobile="47px"
            disabled={!isFormFilled || hasValidationErrors}
          >
            {contactsPostState?.state === "loading" ? (
              <CircularProgress size={24} sx={{ color: "var(--white)" }} />
            ) : (
              "Створити"
            )}
          </Btn>
        </div>
      </Container>
    </Popup>
  );
}